import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GoalContext = createContext(null);

export function GoalProvider({ children }) {
  const { user } = useAuth() || {};
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.role !== "client") return;
    setLoading(true);
    try {
      const { data } = await api.get("/client/goal");
      setGoal(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Push the current theme into CSS vars on <html> so every page picks it up.
  useEffect(() => {
    if (!goal?.theme) return;
    const t = goal.theme;
    const root = document.documentElement;
    root.style.setProperty("--cq-goal-primary", t.primary);
    root.style.setProperty("--cq-goal-primary-soft", t.primary_soft);
    root.style.setProperty("--cq-goal-accent", t.accent);
    root.style.setProperty("--cq-goal-accent-soft", t.accent_soft);
    root.style.setProperty("--cq-goal-bg", t.bg_gradient);
    root.setAttribute("data-cq-goal-theme", t.id);
    return () => {
      root.style.removeProperty("--cq-goal-primary");
      root.style.removeProperty("--cq-goal-primary-soft");
      root.style.removeProperty("--cq-goal-accent");
      root.style.removeProperty("--cq-goal-accent-soft");
      root.style.removeProperty("--cq-goal-bg");
      root.removeAttribute("data-cq-goal-theme");
    };
  }, [goal?.theme?.id]);

  const setGoalText = async (text) => {
    setSaving(true);
    try {
      const { data } = await api.post("/client/goal", { text });
      setGoal(data);
      // Broadcast so features that key off a goal-being-set (walkthrough tour,
      // dashboards, coach) can react immediately without polling.
      try { window.dispatchEvent(new CustomEvent("cq:goal:saved", { detail: data })); } catch {}
      return data;
    } finally { setSaving(false); }
  };

  const regenerateImage = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/client/goal/regenerate-image");
      setGoal(data);
    } finally { setSaving(false); }
  };

  const setTheme = async (theme_id) => {
    const { data } = await api.post("/client/goal/theme", { theme_id });
    setGoal(data);
  };

  const togglePopups = async () => {
    const { data } = await api.post("/client/goal/toggle-popups");
    setGoal((g) => (g ? { ...g, popups_paused: data.popups_paused } : g));
    return data.popups_paused;
  };

  const dismissPopup = async (pid) => {
    try { await api.post(`/client/goal/dismiss-popup/${pid}`); } catch {}
    setGoal((g) => (g ? { ...g, dismissed_popup_ids: [...(g.dismissed_popup_ids || []), pid] } : g));
  };

  const value = { goal, loading, saving, load, setGoalText, regenerateImage, setTheme, togglePopups, dismissPopup };
  return <GoalContext.Provider value={value}>{children}</GoalContext.Provider>;
}

export function useGoal() {
  return useContext(GoalContext) || {};
}
